import React, { useEffect, useState } from "react";
import axios from "axios";
import { ethers } from "ethers";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { SERVER_NAME } from "./config";

import E3DTokenABI from "./E3DToken_ABI.json";
import NFTManagerABI from "./E3DNFTManager_ABI.json";
import { getSubscriptionDetails } from "./web3/contractInteractions";

const E3DToken_ADDRESS = "0x6488861b401F427D13B6619C77C297366bCf6386";
const NFTManager_ADDRESS = "0xeED4620ff525101Ffcf7327378232CA9EF778D47";
const MINT_FEE = ethers.utils.parseUnits("100", 18);

const normalizeEndpointId = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object") {
    if (v.id != null) return String(v.id);
    if (v._id != null) return String(v._id);
  }
  return "";
};

const snapshotPcapGraph = (graphData) => {
  const gd = graphData && typeof graphData === "object" ? graphData : null;
  const nodesIn = gd && Array.isArray(gd.nodes) ? gd.nodes : [];
  const linksIn = gd && Array.isArray(gd.links) ? gd.links : [];

  const nodes = nodesIn.map((n) => {
    const node = n && typeof n === "object" ? n : {};
    return {
      id: node.id != null ? String(node.id) : "",
      label: node.label != null ? String(node.label) : "",
      x: typeof node.x === "number" ? node.x : null,
      y: typeof node.y === "number" ? node.y : null,
      z: typeof node.z === "number" ? node.z : null,
      metaType: node.metaType != null ? String(node.metaType) : undefined,
      type: node.type != null ? String(node.type) : undefined,
      ip: node.ip != null ? String(node.ip) : undefined,
      mac: node.mac != null ? String(node.mac) : undefined,
      oui: node.oui != null ? String(node.oui) : undefined,
    };
  });

  const links = linksIn.map((l) => {
    const link = l && typeof l === "object" ? l : {};
    return {
      source: normalizeEndpointId(link.source),
      target: normalizeEndpointId(link.target),
      metaType: link.metaType != null ? String(link.metaType) : undefined,
      bytes: typeof link.bytes === "number" ? link.bytes : undefined,
      packets: typeof link.packets === "number" ? link.packets : undefined,
      rxBytes: typeof link.rxBytes === "number" ? link.rxBytes : undefined,
      txBytes: typeof link.txBytes === "number" ? link.txBytes : undefined,
    };
  });

  return {
    version: 1,
    kind: "pcap_graph",
    createdAt: new Date().toISOString(),
    nodes,
    links,
  };
};

const captureGraphAsPNG = async (graphRef) => {
  if (!graphRef?.current) return null;

  const renderer = graphRef.current.renderer();
  const scene = graphRef.current.scene();
  const camera = graphRef.current.camera();

  renderer.render(scene, camera);

  const gl = renderer.getContext();
  const width = gl.drawingBufferWidth;
  const height = gl.drawingBufferHeight;

  const pixels = new Uint8Array(width * height * 4);
  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");

  const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
  context.putImageData(imageData, 0, 0);
  context.translate(0, canvas.height);
  context.scale(1, -1);
  context.drawImage(canvas, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob || null), "image/png");
  });
};

export default function MintPcapNFTDialog(props) {
  const { open, onClose, setShowMintPcapNFTDialog, userAddress, graphRef, graphData, pcapCsvText } = props || {};

  const [nftName, setNftName] = useState("");
  const [nftDescription, setNftDescription] = useState("");
  const [isMinting, setIsMinting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const [hasFreeMints, setHasFreeMints] = useState(false);
  const [remainingFreeMints, setRemainingFreeMints] = useState(0);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  const [includePcapFile, setIncludePcapFile] = useState(false);
  const [pcapFile, setPcapFile] = useState(null);
  const pcapFileInputRef = React.useRef(null);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!open || !userAddress) return;

      setIsLoadingSubscription(true);
      try {
        const details = await getSubscriptionDetails(userAddress);
        if (details && details.isActive && details.remainingMints > 0) {
          setHasFreeMints(true);
          setRemainingFreeMints(details.remainingMints);
        } else {
          setHasFreeMints(false);
          setRemainingFreeMints(0);
        }
      } catch (e) {
        setHasFreeMints(false);
        setRemainingFreeMints(0);
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    checkSubscription();
  }, [open, userAddress]);

  const uploadPcapBundleToIPFS = async (thumbnailBlob, name, description, pcapGraphJson, pcapCsvRaw, pcapFileToUpload) => {
    const formData = new FormData();
    formData.append("thumbnail", thumbnailBlob, "pcap.png");
    formData.append("name", name);
    formData.append("description", description);
    formData.append("pcapGraph", JSON.stringify(pcapGraphJson));
    if (!pcapFileToUpload && pcapCsvRaw && String(pcapCsvRaw).trim()) {
      formData.append("pcapCsv", String(pcapCsvRaw));
    }

    if (pcapFileToUpload) {
      formData.append("pcapFile", pcapFileToUpload, pcapFileToUpload.name || 'capture.pcap');
    }

    const response = await axios.post(`${SERVER_NAME}/uploadToIPFS/pcap`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (response.data && response.data.success && response.data.metadata) {
      return response.data.metadata;
    }

    throw new Error((response.data && response.data.error) || "Upload failed");
  };

  const mintNFT = async (metadataURI) => {
    if (!window.ethereum) throw new Error("MetaMask is not installed.");
    await window.ethereum.request({ method: "eth_requestAccounts" });

    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, signer);

    const ensureE3dApproval = async () => {
      const e3dToken = new ethers.Contract(E3DToken_ADDRESS, E3DTokenABI, signer);
      const from = await signer.getAddress();
      const currentAllowance = await e3dToken.allowance(from, NFTManager_ADDRESS);
      if (currentAllowance && currentAllowance.gte && currentAllowance.gte(MINT_FEE)) {
        return;
      }
      setStatusMessage("Approving E3D token spending...");
      const approvalTx = await e3dToken.approve(NFTManager_ADDRESS, MINT_FEE);
      await approvalTx.wait();
    };

    if (!hasFreeMints) {
      await ensureE3dApproval();
    } else {
      setStatusMessage("Using free mint from your premium subscription...");
    }

    setStatusMessage("Minting NFT...");
    let mintTx;
    try {
      mintTx = await nftContract.mintNFT(metadataURI, []);
    } catch (e) {
      const msg = e && e.message ? String(e.message) : '';
      const reason = e && e.reason ? String(e.reason) : '';
      const combined = (reason + ' ' + msg).toLowerCase();
      if (combined.includes('approve e3d tokens first')) {
        await ensureE3dApproval();
        setStatusMessage("Minting NFT...");
        mintTx = await nftContract.mintNFT(metadataURI, []);
      } else {
        throw e;
      }
    }
    setStatusMessage("Transaction submitted. Waiting for confirmation...");
    const receipt = await mintTx.wait();

    if (hasFreeMints) {
      try {
        const updatedDetails = await getSubscriptionDetails(userAddress);
        setRemainingFreeMints(updatedDetails?.remainingMints || 0);
        setHasFreeMints((updatedDetails?.remainingMints || 0) > 0);
      } catch (e) {
      }
    }

    const tokenId = receipt.events?.find((e) => e.event === "Transfer")?.args?.tokenId.toString();
    return { tokenId };
  };

  const handleMint = async () => {
    if (!userAddress) {
      setStatusMessage("Please connect your wallet first.");
      return;
    }

    if (!nftName.trim()) {
      setStatusMessage("NFT Name is required.");
      return;
    }

    setIsMinting(true);
    setStatusMessage("");

    try {
      const thumbnailBlob = await captureGraphAsPNG(graphRef);
      if (!thumbnailBlob) throw new Error("Failed to capture graph");

      const gd = (graphRef && graphRef.current && typeof graphRef.current.graphData === 'function')
        ? graphRef.current.graphData()
        : graphData;

      const pcapGraphJson = snapshotPcapGraph(gd);

      if (includePcapFile && !pcapFile) {
        throw new Error('Please select a .pcap file.');
      }

      setStatusMessage("Uploading PCAP NFT bundle to IPFS...");
      const metadataURI = await uploadPcapBundleToIPFS(
        thumbnailBlob,
        nftName,
        nftDescription,
        pcapGraphJson,
        pcapCsvText,
        includePcapFile ? pcapFile : null
      );

      const result = await mintNFT(metadataURI);

      setStatusMessage("NFT Minted Successfully!");
      setTimeout(() => {
        if (typeof setShowMintPcapNFTDialog === 'function') setShowMintPcapNFTDialog(false);
        if (typeof onClose === 'function') onClose();
      }, 1500);

      return result;
    } catch (error) {
      setStatusMessage(`Minting failed: ${error && error.message ? error.message : 'Unknown error'}`);
    } finally {
      setIsMinting(false);
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="sm"
      scroll="body"
      fullWidth={false}
      style={{ zIndex: 8888 }}
      PaperProps={{
        style: {
          border: '1px solid #4d79ff',
          boxShadow: '0 0 0 1px #4d79ff',
          borderRadius: '15px',
          overflow: 'visible',
          maxWidth: '520px',
          margin: '0 auto',
          boxSizing: 'border-box',
          backgroundColor: '#1f2026',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        },
      }}
      BackdropProps={{
        style: { backgroundColor: 'rgba(0,0,0,0.7)' },
      }}
    >
      <DialogTitle style={{ backgroundColor: '#1f2026', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" style={{ fontWeight: 700, color: '#ffffff' }}>
              Publish PCAP Graph
            </Typography>
            <Typography variant="body2" style={{ color: '#b9c2d0', marginTop: 4 }}>
              Create a signed, permanent record of this graph.
            </Typography>
          </div>
          <IconButton onClick={onClose} style={{ color: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      {isLoadingSubscription ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={2} style={{ backgroundColor: '#1f2026' }}>
          <CircularProgress size={24} style={{ color: '#4d79ff' }} />
          <Typography variant="body2" style={{ marginLeft: 10, color: '#ffffff' }}>
            Checking subscription status...
          </Typography>
        </Box>
      ) : hasFreeMints ? (
        <Box
          bgcolor="#252525"
          p={2}
          mx={3}
          mb={2}
          borderRadius="12px"
          style={{ border: '1px solid rgba(77, 121, 255, 0.35)' }}
        >
          <Box display="flex" alignItems="center">
            <CheckCircleIcon style={{ color: '#4fc3f7', marginRight: 8 }} />
            <Typography variant="body1" style={{ fontWeight: 700, color: '#ffffff' }}>
              Included with Premium
            </Typography>
          </Box>
          <Typography variant="body2" style={{ color: '#dddddd', marginTop: 8 }}>
            You can publish{' '}
            <Chip
              label={remainingFreeMints}
              size="small"
              style={{ margin: '0 4px', backgroundColor: '#4fc3f7', color: 'white', fontWeight: 700 }}
            />
            more graphs this month.
          </Typography>
        </Box>
      ) : null}

      <DialogContent style={{ backgroundColor: '#1f2026', paddingTop: 8, paddingBottom: 20 }}>
        <Box
          mx={1}
          style={{
            backgroundColor: '#1a1a1a',
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
        <TextField
          fullWidth
          label="Graph Title"
          placeholder="e.g. Suspicious Lateral Movement - Jan 12"
          value={nftName}
          onChange={(e) => setNftName(e.target.value)}
          margin="dense"
          variant="outlined"
          InputLabelProps={{ style: { color: '#dddddd' } }}
          InputProps={{
            style: { color: '#ffffff', backgroundColor: '#2c2c2c', borderRadius: 12 },
          }}
        />
        <TextField
          fullWidth
          label="Graph Description"
          placeholder="What does this graph show? Why does it matter?"
          multiline
          rows={3}
          value={nftDescription}
          onChange={(e) => setNftDescription(e.target.value)}
          margin="dense"
          variant="outlined"
          InputLabelProps={{ style: { color: '#dddddd' } }}
          InputProps={{
            style: { color: '#ffffff', backgroundColor: '#2c2c2c', borderRadius: 12 },
          }}
        />

        <div style={{ marginTop: 10 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includePcapFile}
                onChange={(e) => {
                  const next = !!e.target.checked;
                  setIncludePcapFile(next);
                  if (!next) setPcapFile(null);
                }}
                color="primary"
              />
            }
            label="Attach .pcap file (instead of CSV)"
            style={{ color: '#dddddd' }}
          />

          {includePcapFile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
              <input
                ref={pcapFileInputRef}
                type="file"
                accept=".pcap"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e && e.target && e.target.files && e.target.files.length ? e.target.files[0] : null;
                  setPcapFile(f);
                }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  if (pcapFileInputRef && pcapFileInputRef.current) pcapFileInputRef.current.click();
                }}
                style={{ borderColor: 'rgba(77, 121, 255, 0.6)', color: '#ffffff', textTransform: 'none' }}
              >
                Select File
              </Button>
              <Typography variant="body2" style={{ color: '#b9c2d0', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {pcapFile ? pcapFile.name : 'No file selected'}
              </Typography>
            </div>
          ) : null}
        </div>

        <Button
          variant="contained"
          color="primary"
          onClick={handleMint}
          disabled={isMinting || !nftName.trim()}
          style={{
            marginTop: 16,
            width: '100%',
            padding: '12px 0',
            borderRadius: 12,
            fontSize: '1.05rem',
            fontWeight: 700,
            textTransform: 'none',
            backgroundColor: hasFreeMints ? '#4fc3f7' : '#4d79ff',
            color: '#fff',
          }}
          startIcon={hasFreeMints ? <CheckCircleIcon /> : null}
        >
          {isMinting ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 10, color: 'white' }} />
              Processing...
            </>
          ) : (
            'Sign & Publish'
          )}
        </Button>

        <Typography variant="body2" style={{ marginTop: 10, color: '#b9c2d0', textAlign: 'center' }}>
          This creates a permanent, signed version of this graph you can share or list later.
        </Typography>

        {statusMessage && (
          <Typography
            variant="body2"
            style={{
              marginTop: 15,
              color: statusMessage.toLowerCase().includes('failed') ? '#ff6961' : '#77dd77',
              textAlign: 'center',
            }}
          >
            {statusMessage}
          </Typography>
        )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
